import { useState } from 'react';
import { Plus, Pencil, Trash2, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Switch } from '@/components/ui/switch';
import { usePipelinesSettings, type Pipeline } from '@/hooks/usePipelinesSettings';
import { PipelineStagesManager } from './PipelineStagesManager';

export function PipelinesTab() {
  const {
    pipelines,
    loading,
    createPipeline,
    updatePipeline,
    deletePipeline
  } = usePipelinesSettings();

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedPipeline, setSelectedPipeline] = useState<Pipeline | null>(null);
  const [isStagesDialogOpen, setIsStagesDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    is_active: true
  });

  const handleOpenDialog = (pipeline?: Pipeline) => {
    if (pipeline) {
      setSelectedPipeline(pipeline);
      setFormData({
        name: pipeline.name,
        description: pipeline.description || '',
        is_active: pipeline.is_active
      });
    } else {
      setSelectedPipeline(null);
      setFormData({
        name: '',
        description: '',
        is_active: true
      });
    }
    setIsDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setSelectedPipeline(null);
    setFormData({
      name: '',
      description: '',
      is_active: true
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (selectedPipeline) {
      await updatePipeline(selectedPipeline.id, formData);
    } else {
      await createPipeline(formData);
    }

    handleCloseDialog();
  };

  const handleDelete = async (id: string) => {
    if (confirm('Tem certeza que deseja excluir este pipeline e todos os seus estágios?')) {
      await deletePipeline(id);
    }
  };

  const handleOpenStages = (pipeline: Pipeline) => {
    setSelectedPipeline(pipeline);
    setIsStagesDialogOpen(true);
  };

  const handleCloseStages = () => {
    setIsStagesDialogOpen(false);
    setSelectedPipeline(null);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-muted-foreground">Carregando...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-end items-center">
        <Button onClick={() => handleOpenDialog()} className="bg-[#E4E4E7] text-black hover:bg-[#D4D4D7]">
          <Plus className="w-4 h-4 mr-2" />
          Novo Pipeline
        </Button>
      </div>

      {pipelines.length === 0 ? (
        <div className="text-center py-12 border-2 border-dashed border-gray-600 rounded-lg">
          <p className="text-gray-400 mb-4">Nenhum pipeline cadastrado</p>
          <Button onClick={() => handleOpenDialog()} className="bg-[#E4E4E7] text-black hover:bg-[#D4D4D7]">
            <Plus className="w-4 h-4 mr-2" />
            Criar Primeiro Pipeline
          </Button>
        </div>
      ) : (
        <div className="grid gap-4">
          {pipelines.map((pipeline) => (
            <div
              key={pipeline.id}
              className="border border-gray-600 bg-neutral-700/50 rounded-lg p-4 hover:bg-neutral-600/50 transition-colors"
            >
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3">
                    <h3 className="font-semibold text-white">{pipeline.name}</h3>
                    {!pipeline.is_active && (
                      <span className="text-xs bg-gray-600 text-gray-300 px-2 py-1 rounded">Inativo</span>
                    )}
                  </div>
                  {pipeline.description && (
                    <p className="text-sm text-gray-400 mt-1">
                      {pipeline.description}
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleOpenStages(pipeline)}
                    className="text-white hover:bg-neutral-600"
                  >
                    <ChevronRight className="w-4 h-4 mr-1" />
                    Estágios
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleOpenDialog(pipeline)}
                    className="text-white hover:bg-neutral-600"
                  >
                    <Pencil className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDelete(pipeline.id)}
                    className="text-red-400 hover:bg-neutral-600 hover:text-red-300"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="bg-[#18181B] border-[#3F3F464D]">
          <DialogHeader>
            <DialogTitle className="text-white">
              {selectedPipeline ? 'Editar Pipeline' : 'Novo Pipeline'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="name" className="text-white">Nome *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Ex: Vendas, Atendimento..."
                className="bg-neutral-600 text-white border-[#3F3F46] placeholder:text-gray-400"
                required
              />
            </div>

            <div>
              <Label htmlFor="description" className="text-white">Descrição</Label>
              <Input
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Descrição do pipeline"
                className="bg-neutral-600 text-white border-[#3F3F46] placeholder:text-gray-400"
              />
            </div>

            <div className="flex items-center justify-between">
              <Label htmlFor="is_active" className="text-white">Ativo</Label>
              <Switch
                id="is_active"
                checked={formData.is_active}
                onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={handleCloseDialog} className="border-white text-white hover:bg-white hover:text-black">
                Cancelar
              </Button>
              <Button type="submit" className="bg-[#E4E4E7] text-black hover:bg-[#D4D4D7]">
                {selectedPipeline ? 'Salvar' : 'Criar'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {selectedPipeline && (
        <PipelineStagesManager
          pipeline={selectedPipeline}
          open={isStagesDialogOpen}
          onClose={handleCloseStages}
        />
      )}
    </div>
  );
}
